import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import API_BASE_URL from '../../Config';

const BalanceGeneral = () => {
    const [balanceData, setBalanceData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fechaFin, setFechaFin] = useState(format(new Date(), 'yyyy-MM-dd'));

    const fetchBalance = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/contabilidad/balance-general?fin=${fechaFin}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBalanceData(res.data);
        } catch (error) {
            console.error("Error cargando el balance general", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBalance();
    }, []);

    const renderGrupo = (titulo, items, total, bgClass) => (
        <div className="card mb-4">
            <div className={`card-header ${bgClass} text-white`}>
                <h5 className="mb-0">{titulo}</h5>
            </div>
            <div className="card-body p-0">
                <table className="table table-hover mb-0">
                    <tbody>
                        {items && items.length > 0 ? (
                            items.map((item, idx) => (
                                <tr key={idx}>
                                    <td width="20%" className="text-center">{item.codigoIncoop}</td>
                                    <td width="50%">{item.nombreCuenta}</td>
                                    <td width="30%" className="text-right">{new Intl.NumberFormat('es-PY').format(item.saldo)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="3" className="text-center text-muted">Sin movimientos</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="table-active font-weight-bold">
                            <td colSpan="2" className="text-right">TOTAL {titulo.toUpperCase()}</td>
                            <td className="text-right">{new Intl.NumberFormat('es-PY').format(total || 0)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );

    return (
        <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-2 d-flex justify-content-between align-items-center">
                <h3 className="mb-0 text-gray-800 font-weight-bold">Balance General</h3>
                <div className="d-flex align-items-center gap-2">
                    <div className="input-group input-group-sm shadow-sm" style={{ width: '220px' }}>
                        <div className="input-group-prepend">
                            <span className="input-group-text bg-white"><i className="fas fa-calendar-alt text-primary"></i></span>
                        </div>
                        <input 
                            type="date" 
                            value={fechaFin} 
                            onChange={e => setFechaFin(e.target.value)} 
                            className="form-control" 
                        />
                    </div>
                    <button className="btn btn-primary btn-sm px-3 shadow-sm" onClick={fetchBalance} disabled={loading}>
                        <i className="fas fa-sync-alt mr-2"></i> Generar
                    </button>
                </div>
            </div>
            
            <div className="card-body bg-gray-50 pt-3">
                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-3 text-muted">Calculando situación financiera...</p>
                    </div>
                ) : balanceData ? (
                    <>
                        {!balanceData.cuadra && (
                            <div className="alert alert-soft-danger border-danger shadow-sm mb-4 animate__animated animate__headShake">
                                <i className="fas fa-exclamation-circle mr-2"></i> 
                                <strong>Descuadre Detectado:</strong> La ecuación contable (A = P + PT) no está equilibrada. Verifique asientos manuales y cierres.
                            </div>
                        )}
                        <div className="row">
                            {/* Columna Izquierda: Activos */}
                            <div className="col-lg-6">
                                <div className="card shadow-sm border-0 mb-4 h-100">
                                    <div className="card-header bg-success text-white py-2">
                                        <h6 className="mb-0 font-weight-bold uppercase tracking-wider">I. ACTIVOS</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="px-3 border-top-0">CÓDIGO</th>
                                                    <th className="border-top-0">CUENTA</th>
                                                    <th className="text-right px-3 border-top-0">SALDO</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {balanceData.activos && balanceData.activos.length > 0 ? (
                                                    balanceData.activos.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td className="px-3 text-muted font-mono" style={{ fontSize: '0.75rem' }}>{item.codigoIncoop}</td>
                                                            <td>{item.nombreCuenta}</td>
                                                            <td className="text-right px-3 font-weight-bold font-mono text-success">{new Intl.NumberFormat('es-PY').format(item.saldo)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="3" className="text-center py-4 text-muted small">No hay activos registrados</td></tr>
                                                )}
                                            </tbody>
                                            <tfoot className="bg-light font-weight-bold">
                                                <tr>
                                                    <td colSpan="2" className="text-right px-3 py-2">TOTAL ACTIVOS:</td>
                                                    <td className="text-right px-3 py-2 h6 mb-0 font-weight-bold text-success border-top-2">{new Intl.NumberFormat('es-PY').format(balanceData.totalActivos || 0)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Columna Derecha: Pasivos y Patrimonio */}
                            <div className="col-lg-6">
                                {/* Pasivos */}
                                <div className="card shadow-sm border-0 mb-4">
                                    <div className="card-header bg-danger text-white py-2">
                                        <h6 className="mb-0 font-weight-bold uppercase tracking-wider">II. PASIVOS</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                                            <tbody>
                                                {balanceData.pasivos && balanceData.pasivos.length > 0 ? (
                                                    balanceData.pasivos.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td width="20%" className="px-3 text-muted font-mono" style={{ fontSize: '0.75rem' }}>{item.codigoIncoop}</td>
                                                            <td width="50%">{item.nombreCuenta}</td>
                                                            <td width="30%" className="text-right px-3 font-weight-bold font-mono text-danger">{new Intl.NumberFormat('es-PY').format(item.saldo)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="3" className="text-center py-3 text-muted small">No hay pasivos registrados</td></tr>
                                                )}
                                            </tbody>
                                            <tfoot className="bg-light font-weight-bold">
                                                <tr>
                                                    <td colSpan="2" className="text-right px-3 py-2">TOTAL PASIVOS:</td>
                                                    <td className="text-right px-3 py-2 text-danger">{new Intl.NumberFormat('es-PY').format(balanceData.totalPasivos || 0)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                {/* Patrimonio */}
                                <div className="card shadow-sm border-0 mb-0">
                                    <div className="card-header bg-primary text-white py-2">
                                        <h6 className="mb-0 font-weight-bold uppercase tracking-wider">III. PATRIMONIO NETO</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                                            <tbody>
                                                {balanceData.patrimonio && balanceData.patrimonio.length > 0 ? (
                                                    balanceData.patrimonio.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td width="20%" className="px-3 text-muted font-mono" style={{ fontSize: '0.75rem' }}>{item.codigoIncoop}</td>
                                                            <td width="50%">{item.nombreCuenta}</td>
                                                            <td width="30%" className="text-right px-3 font-weight-bold font-mono text-primary">{new Intl.NumberFormat('es-PY').format(item.saldo)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="3" className="text-center py-3 text-muted small">No hay patrimonio registrado</td></tr>
                                                )}
                                            </tbody>
                                            <tfoot className="bg-light font-weight-bold">
                                                <tr>
                                                    <td colSpan="2" className="text-right px-3 py-2">TOTAL PATRIMONIO:</td>
                                                    <td className="text-right px-3 py-2 text-primary">{new Intl.NumberFormat('es-PY').format(balanceData.totalPatrimonio || 0)}</td>
                                                </tr>
                                                <tr className="bg-dark text-white">
                                                    <td colSpan="2" className="text-right px-3 py-2">TOTAL PASIVO + PATRIMONIO:</td>
                                                    <td className="text-right px-3 py-2 h6 mb-0 font-weight-bold">{new Intl.NumberFormat('es-PY').format((balanceData.totalPasivos + balanceData.totalPatrimonio) || 0)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Fila de Resumen Final */}
                        <div className="row mt-4 mb-2">
                            <div className="col-12">
                                <div className={`p-3 rounded shadow-sm border-left-4 ${balanceData.cuadra ? 'bg-white border-success' : 'bg-white border-danger'} d-flex justify-content-between align-items-center`}>
                                    <div>
                                        <h5 className="mb-0 font-weight-bold text-gray-800">Resultado de Ecuación Contable</h5>
                                        <span className={`small ${balanceData.cuadra ? 'text-success' : 'text-danger'}`}>
                                            {balanceData.cuadra ? 'La situación financiera está balanceada (Activo = Pasivo + Patrimonio)' : 'Existe una diferencia que debe ser auditada.'}
                                        </span>
                                    </div>
                                    <div className="h4 mb-0 font-weight-bold text-gray-900">
                                        Diferencia: {new Intl.NumberFormat('es-PY').format(Math.abs(balanceData.totalActivos - (balanceData.totalPasivos + balanceData.totalPatrimonio)))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center p-5 text-muted">No se pudo recuperar la información del balance general.</div>
                )}
            </div>
        </div>
    );
};

export default BalanceGeneral;
